import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import { MissionIcon } from '@app/mission/MissionIcon';
import { useMissionChartData } from '@domain/mission/useMissionChartData';

export const MissionChart = () => {
    const { data, isPending, isError, error } = useMissionChartData();

    const months = useMemo(() => {
        if (!data) return [];

        return data.map(item => ({
            date: format(new Date(item.month), 'MMM'),
            upcoming: item.upcomingMissions,
            past: item.pastMissions,
        }));
    }, [data]);

    if (isPending) return null; // FIXME return skeleton instead

    if (isError) {
        console.error('Mission chart failed to load', error);
        return null;
    }

    const range = months.reduce(
        (acc, month) => {
            acc.min = Math.min(acc.min, month.past);
            acc.max = Math.max(acc.max, month.upcoming + month.past);
            return acc;
        },
        { min: 0, max: 0 }
    );

    return (
        <Stack>
            <Box display="flex" alignItems="center" mb={1}>
                <Box mr={1} display="flex">
                    <MissionIcon />
                </Box>
                <Typography variant="h5" color="textSecondary">
                    Mission Timeline
                </Typography>
                <Tooltip title="Create mission">
                    <IconButton
                        size="small"
                        sx={{
                            color: 'text.secondary',
                            ml: 'auto',
                        }}
                        component={RouterLink}
                        to="/mission/create"
                    >
                        <ControlPointIcon fontSize="inherit" color="primary" />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box height={300}>
                <ResponsiveBar
                    data={months}
                    indexBy="date"
                    keys={['upcoming', 'past']}
                    colors={['#61cdbb', '#e25c3b']}
                    margin={{ top: 30, right: 50, bottom: 30, left: 0 }}
                    padding={0.3}
                    valueScale={{
                        type: 'linear',
                        min: 0,
                        max: range.max,
                    }}
                    indexScale={{ type: 'band', round: true }}
                    enableGridX={true}
                    enableGridY={false}
                    enableLabel={false}
                    axisTop={{
                        tickSize: 0,
                        tickPadding: 12,
                    }}
                    axisBottom={{
                        legendPosition: 'middle',
                        legendOffset: 50,
                        tickSize: 0,
                        tickPadding: 12,
                    }}
                    axisLeft={null}
                    axisRight={{
                        format: (v: any) => `${Math.abs(v)}`,
                        tickValues: 8,
                    }}
                    markers={
                        [
                            {
                                axis: 'y',
                                value: 0,
                                lineStyle: { strokeOpacity: 0 },
                                textStyle: { fill: '#2ebca6' },
                                legend: 'Upcoming',
                                legendPosition: 'top-right',
                                legendOrientation: 'vertical',
                                legendOffsetX: 10,
                                legendOffsetY: 0,
                            },
                            {
                                axis: 'y',
                                value: 0,
                                lineStyle: {
                                    stroke: '#f47560',
                                    strokeWidth: 1,
                                },
                                textStyle: { fill: '#e25c3b' },
                                legend: 'Past',
                                legendPosition: 'top-left',
                                legendOrientation: 'vertical',
                                legendOffsetX: 10,
                                legendOffsetY: 0,
                            },
                        ] as any
                    }
                />
            </Box>
        </Stack>
    );
};
